/**
 * useGlobalSearch — F-SEARCH-1, erweitert in Runde 6 (HD)
 *
 * Sucht via GET /api/v2/search?q=<query>.
 *
 * WICHTIG (bewusste Abweichung von v1): die Suche ist STRENG USER-SCOPED.
 * v1 (app/controllers/search_controller.rb:8-17) durchsuchte global ALLE
 * Hotspots des Netzwerks. v2 durchsucht ausschliesslich die eigenen Daten des
 * eingeloggten Users — Hotspots, Wallets, Kontakte und Tags. Eine oeffentliche
 * Netzwerksuche wird bewusst NICHT angeboten (Datenschutz + Missbrauchs-/
 * Performance-Risiko ueber die komplette Hotspot-Tabelle).
 *
 * Backend-Contract: alle vier Gruppen sind immer vorhanden (auch leer);
 * q < 2 oder > 64 Zeichen liefert leere Gruppen mit Status 200; Limit 8 je
 * Gruppe.
 *
 * SICHERHEIT: Contact.name und Tag.name sind im Backend raw_string_fields
 * (kein strip_tags). Die Werte MUESSEN als Text gerendert werden — niemals
 * ueber dangerouslySetInnerHTML.
 *
 * Debouncing obliegt dem Aufrufer (CommandPalette setzt 250ms-Debounce).
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../lib/api-client'

export type SearchHotspot = {
  id: number
  name: string
  address: string
}

export type SearchWallet = {
  id: number
  name: string
  address: string
}

export type SearchContact = {
  id: number
  name: string
  email: string | null
}

export type SearchTag = {
  id: number
  name: string
}

export type SearchHost = {
  id: number
  name: string
  email: string | null
}

export type SearchNote = {
  id: number
  title: string
  text: string
}

export type SearchResults = {
  hotspots: SearchHotspot[]
  wallets: SearchWallet[]
  contacts: SearchContact[]
  tags: SearchTag[]
  hosts: SearchHost[]
  notes: SearchNote[]
}

const EMPTY_RESULTS: SearchResults = { hotspots: [], wallets: [], contacts: [], tags: [], hosts: [], notes: [] }

/** Backend liefert alle Gruppen, aeltere Deployments evtl. nur hotspots/wallets. */
export function normalizeSearchResults(raw: Partial<SearchResults> | undefined): SearchResults {
  return {
    hotspots: raw?.hotspots ?? EMPTY_RESULTS.hotspots,
    wallets: raw?.wallets ?? EMPTY_RESULTS.wallets,
    contacts: raw?.contacts ?? EMPTY_RESULTS.contacts,
    tags: raw?.tags ?? EMPTY_RESULTS.tags,
    hosts: raw?.hosts ?? EMPTY_RESULTS.hosts,
    notes: raw?.notes ?? EMPTY_RESULTS.notes,
  }
}

// web#22: Backend-Performance/-Abdeckung der Suche wird hier NICHT behoben —
// nur der Client-seitige Endlos-"Suche..."-Zustand, wenn der Request haengt.
const SEARCH_TIMEOUT_MS = 8000

export function useGlobalSearch(query: string) {
  return useQuery<SearchResults>({
    queryKey: ['global-search', query],
    queryFn: async ({ signal }) => {
      // Eigener Timeout-Controller: bricht sowohl bei Query-Cancel (debouncedQuery
      // aendert sich / Palette schliesst) als auch nach SEARCH_TIMEOUT_MS ab, statt
      // unbegrenzt auf ein haengendes Backend zu warten.
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), SEARCH_TIMEOUT_MS)
      signal.addEventListener('abort', () => controller.abort())
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const client = apiClient as any
        const result = await client.GET('/api/v2/search', {
          params: { query: { q: query } },
          signal: controller.signal,
        })
        if (result.error) throw result.error
        return normalizeSearchResults(result.data as Partial<SearchResults> | undefined)
      } finally {
        clearTimeout(timeoutId)
      }
    },
    enabled: query.length >= 2,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: false,
  })
}
